/* eslint-disable no-console */
import { Badge, Box, Button, Checkbox, Flex, useToast } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetBbplMonitorStatusLazyQueryGQLType } from '../requests/bbplMonitor.gql.generated';
import { BBPLMonitorParams } from '../types/bbplMonitorTypes';
import { MonitorModeControls } from 'packages/common-frontend/components/MonitorModeControls';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';

type ReportingDateAndFileVersion = {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};

type UserSelection = ReportingDateAndFileVersion & {
  pollForUpdates: boolean;
};

// selection of options for BBPL Monitor
// this will let user select the reporting date and file version
// it populates the options in BBPLMonitorParams and calls onChange
// @bbplMonitor: add creditFacility selector here, when
// working in lender mode.
export const BBPLMonitorOptions: React.FC<{
  initialParams: BBPLMonitorParams | null;
  organizationId: string;
  onChange: (params: BBPLMonitorParams | null) => void;
}> = ({ organizationId, initialParams, onChange }) => {
  const toast = useToast();
  const history = useHistory();
  const defaultDateStr = commonDate.currentDateStr();

  const [latestArtifactDocumentId, setLatestArtifactDocumentId] = useState<
    string | null
  >(null);

  const initialReportingDate = initialParams?.reportingDate || defaultDateStr;
  const initialFileVersion = initialParams?.fileVersion || 1;

  // latest for reporting date and file version for which we have the artifact instances
  const [lastest, setLastest] = useState<ReportingDateAndFileVersion | null>(
    null,
  );

  // user selection of reporting date/file version.
  const [selected, setSelected] = useState<UserSelection>({
    reportingDate: initialReportingDate,
    fileVersion: initialFileVersion,
    pollForUpdates: false,
  });

  const [
    queryStatus,
    { loading: loadingArtifactInstances },
  ] = useGetBbplMonitorStatusLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load BBPL monitor page`;
        toast(errorToastConfig);
        history.push('/');
      }
    },
    onCompleted: (data) => {
      const { borrowingBasePipelineStatus } = data;
      const {
        latestArtifactInstance,
        latestArtifactDocumentId: newArtifactDocumentId,
      } = borrowingBasePipelineStatus;
      const reportingDate =
        latestArtifactInstance?.reportingDate || initialReportingDate;
      const fileVersion =
        latestArtifactInstance?.fileVersion || initialFileVersion;

      if (newArtifactDocumentId) {
        setLatestArtifactDocumentId(newArtifactDocumentId);
        if (latestArtifactInstance) {
          setLastest({
            reportingDate,
            fileVersion,
          });
        }
      }
    },
  });

  // on loading the componenet fetch status.
  // this will return the latest artifact document id
  // and latest reporting date and file version for which we have
  // the artifact instances.
  useEffect(() => {
    queryStatus({
      variables: {
        organizationId,
      },
    });
  }, [organizationId, queryStatus]);

  // when user selection changes call onChagne.
  // we do that only when latestArtifactDocumentId is known
  // latestArtifactDocumentId is known only after initial status query is finished.
  useEffect(() => {
    if (latestArtifactDocumentId) {
      onChange({
        organizationId,
        ...selected,
        latestArtifactDocumentId,
      });
    }
  }, [
    latestArtifactDocumentId,
    onChange,
    organizationId,
    selected,
    selected.pollForUpdates,
  ]);

  if (!latestArtifactDocumentId) {
    // we expect to have latestArtifactDocumentId once status query is finieshed.
    return (
      <Box width="100%" border="1px solid #ccc">
        <Flex minWidth="max-content" alignItems="center" gap="2">
          <Badge ml="2" variant="outline" colorScheme="yellow">
            loading...
          </Badge>
        </Flex>
      </Box>
    );
  }

  return (
    <Box width="100%" border="1px solid #ccc">
      <Flex minWidth="max-content" alignItems="center" gap="2">
        <MonitorModeControls
          onMonitor={(
            newReportingDate: Util.FullDateStr,
            newFileVersion: number,
          ) => {
            setSelected({
              ...selected,
              reportingDate: newReportingDate,
              fileVersion: newFileVersion,
            });
          }}
          reportingDate={selected.reportingDate}
          fileVersion={selected.fileVersion}
        />

        {lastest && (
          <Button
            onClick={() => {
              setSelected({
                ...selected,
                ...lastest,
              });
            }}
          >
            Goto Latest
          </Button>
        )}
        <Checkbox
          ml="2"
          isChecked={selected.pollForUpdates}
          onChange={(e) =>
            setSelected({ ...selected, pollForUpdates: e.target.checked })
          }
        >
          Auto-poll
        </Checkbox>

        {loadingArtifactInstances && (
          <Badge ml="2" variant="outline" colorScheme="yellow">
            loading...
          </Badge>
        )}
      </Flex>
    </Box>
  );
};
